import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

const SELECTORS = {
  quizItemGroup: '.js-feelings-monster-quiz__quiz-item-group',
  quizItem: '.js-feelings-monster-quiz__quiz-item',
  quizItemBtn: '.js-feelings-monster-quiz__quiz-item-button',
  quizItemImage: '.js-feelings-monster-quiz__quiz-item-image',
  quizItemModal: '.js-feelings-monster-quiz__quiz-item-modal',
  quizItemCloseBtn: '.js-feelings-monster-quiz__quiz-item-close-button',
  quizPrevBtn: '.js-feelings-monster-quiz__pagination__prev',
  quizNextBtn: '.js-feelings-monster-quiz__pagination__next',
  quizModalOverlay: '.js-feelings-monster-quiz__modal-overlay',
  quizPaginationCounter: '.js-feelings-monster-quiz-pagination-counter',
  externalCta: '.js-feelings-monster-quiz-external-download-cta'
}

const CLASSES = {
  selected: '-selected',
  visuallyHidden: '-visually-hidden'
}

const THEMES = {
  red: {
    light: 'tetradic-ultralight-red-orange',
    dark: 'tetradic-ultradark-red'
  },
  orange: {
    light: 'tetradic-light-orange',
    dark: 'tetradic-ultradark-orange'
  },
  yellow: {
    light: 'tetradic-ultralight-yellow-green',
    dark: 'tetradic-ultradark-red-orange'
  },
  green_yellow: {
    light: 'tetradic-ultralight-green-yellow',
    dark: 'tetradic-ultradark-green-yellow'
  },
  green: {
    light: 'tetradic-ultralight-green',
    dark: 'tetradic-ultradark-green-yellow'
  }
}

const LIGHT_THEME_ELEMENTS = document.querySelectorAll('[data-theme*=light],[data-theme*=core],[data-theme=default]:not(.related-stories):not(.exclude-from-feelings-monster-theme-updates)')
const DARK_THEME_ELEMENTS = document.querySelectorAll('[data-theme*=dark]')
const INITIAL_LIGHT_THEMES = [...LIGHT_THEME_ELEMENTS].map(el => el.dataset.theme)
const INITIAL_DARK_THEMES = [...DARK_THEME_ELEMENTS].map(el => el.dataset.theme)
class FeelingsMonsterQuiz {
  constructor(element) {
    this.element = element
    this.quizItemGroupIndex = 0
    this.isModalOpen = false
    this.quizItemGroups = this.element.querySelectorAll(SELECTORS.quizItemGroup)
    this.quizItems = this.element.querySelectorAll(SELECTORS.quizItem)
    this.quizItemButtons = this.element.querySelectorAll(SELECTORS.quizItemBtn)
    this.quizItemModals = this.element.querySelectorAll(SELECTORS.quizItemModal)
    this.quizItemCloseButtons = this.element.querySelectorAll(SELECTORS.quizItemCloseBtn)
    this.quizPrevBtn = this.element.querySelector(SELECTORS.quizPrevBtn)
    this.quizNextBtn = this.element.querySelector(SELECTORS.quizNextBtn)
    this.quizPaginationCounter = this.element.querySelector(SELECTORS.quizPaginationCounter)
    this.externalDownloadCtaClass = this.element.dataset.externalDownloadCtaClass
    if (this.externalDownloadCtaClass) {
      this.externalCtas = [...document.querySelectorAll('.' + this.externalDownloadCtaClass)].map(cta => cta.querySelector('a') || cta)
      this.initialValuesOfExternalCtaHrefs = this.externalCtas?.map(el => el.href)
      this.initialValuesOfExternalCtaAriaLabels = this.externalCtas?.map(el => el.ariaLabel)
    }
    this.quizModalOverlay = this.element.querySelector(SELECTORS.quizModalOverlay)
    this.observer = this.registerObserver()
    this.observer.observe(this.element)
    this.init()
  }

  init() {
    this.registerEventListeners()
    this.getSkipLink()
  }

  getSkipLink() {
    this.skipLink = document.querySelector('.feelings-monster-quiz__skip-link')

    if (this.element.nextElementSibling) {
      this.skipLink.href = `#${this.element.nextElementSibling.id}`
    } else {
      this.skipLink.remove()
    }
  }

  registerEventListeners() {
    this.quizItemButtons.forEach(btn => {
      btn.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.toggleSelected)
      btn.addEventListener(GLOBAL_CONSTANTS.EVENTS.MOUSEENTER, this.setImageAriaHidden(false))
      btn.addEventListener(GLOBAL_CONSTANTS.EVENTS.MOUSELEAVE, this.setImageAriaHidden(true))
    }

    )
    this.quizItemCloseButtons.forEach(btn => {
      btn.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeModal)
    })
    this.quizModalOverlay.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeModal)
    this.quizNextBtn?.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.showNextGroup)
    this.quizPrevBtn?.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.showPrevGroup)
    document.addEventListener(GLOBAL_CONSTANTS.EVENTS.KEYDOWN, this.escKeyHandler)
    document.addEventListener(GLOBAL_CONSTANTS.EVENTS.KEYDOWN, this.handleTabInModal)
  }

  registerObserver() {
    const options = {
      root: null,
      rootMargin: '0%',
      threshold: 0.5
    }

    return new IntersectionObserver(this.elementInView.bind(this), options)
  }

  // Intersection observer callback, registered above to observe
  // this.element and initiate animation when element is in view
  elementInView(elements) {
    elements.forEach(el => {
      if (el.isIntersecting) {
        this.quizItemGroups[this.quizItemGroupIndex].classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)

        setTimeout(() => {
          this.quizItemGroups[this.quizItemGroupIndex].classList.remove(CLASSES.visuallyHidden)
        }, 20)
      }
    })
  }

  showNextGroup = () => {
    // increment quizItemGroupIndex and wrap to 0 at the end
    this.quizItemGroupIndex = (this.quizItemGroupIndex + 1) % this.quizItemGroups.length
    this.showGroupByIndex(this.quizItemGroupIndex)
  }

  showPrevGroup = () => {
    // decrement quizItemGroupIndex and wrap to 0 at the end
    this.quizItemGroupIndex = (this.quizItemGroupIndex || this.quizItemGroups.length) - 1
    this.showGroupByIndex(this.quizItemGroupIndex)
  }

  showGroupByIndex = (index) => {
    // hide all the groups
    this.quizItemGroups.forEach(this.hide)

    // show the new group and focus the first item
    const newQuizItemGroup = this.quizItemGroups[index]
    this.show(newQuizItemGroup)
    const newQuizItems = newQuizItemGroup.querySelectorAll(SELECTORS.quizItem)
    newQuizItems[0].querySelector(SELECTORS.quizItemBtn).focus()

    // update pagination counter
    this.quizPaginationCounter.textContent = index + 1
  }

  selectItem = (quizItem) => {
    const { downloadUrl: href, downloadText: ariaLabel } = quizItem.dataset
    const quizItemThemeColor = quizItem.dataset.themeColor
    this.setThemes(quizItemThemeColor)

    this.quizModalOverlay.classList.add(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
    quizItem.classList.add(CLASSES.selected)

    const modal = quizItem.nextElementSibling
    modal.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    setTimeout(() => {
      modal.classList.remove(CLASSES.visuallyHidden)
      modal.classList.add(GLOBAL_CONSTANTS.CLASSES.OPEN)
    }, 20)
    this.isModalOpen = true

    const closeBtn = modal.querySelector(SELECTORS.quizItemCloseBtn)
    closeBtn.focus()

    this.setExternalCtaHrefsAndAriaLabels(href, ariaLabel)
  }

  deselectItem = (quizItem) => {
    quizItem.querySelector(SELECTORS.quizItemBtn).focus()
    quizItem.classList.remove(CLASSES.selected)
    this.resetThemes()
  }

  closeModal = () => {
    this.isModalOpen = false
    this.quizModalOverlay.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
    this.quizItemModals.forEach(modal => {
      modal.classList.remove(GLOBAL_CONSTANTS.CLASSES.OPEN)
      modal.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE, CLASSES.visuallyHidden)
    })
    this.element.querySelector(`.${CLASSES.selected} ${SELECTORS.quizItemBtn}`)?.focus()
  }

  setThemes (color) {
    const lightTheme = THEMES[color].light
    const darkTheme = THEMES[color].dark

    LIGHT_THEME_ELEMENTS.forEach(el => el.setAttribute('data-theme', lightTheme))
    DARK_THEME_ELEMENTS.forEach(el => el.setAttribute('data-theme', darkTheme))
  }

  resetThemes () {
    LIGHT_THEME_ELEMENTS.forEach((el, i) => {
      el.setAttribute('data-theme', INITIAL_LIGHT_THEMES[i])
    })
    DARK_THEME_ELEMENTS.forEach((el, i) => {
      el.setAttribute('data-theme', INITIAL_DARK_THEMES[i])
    })
  }

  setExternalCtaHrefsAndAriaLabels = (href, ariaLabel) => {
    this.externalCtas?.forEach(cta => {
      cta.setAttribute('href', href)
      cta.setAttribute('aria-label', ariaLabel)
    })
  }

  resetExternalCtaHrefsAndAriaLabels = () => {
    this.externalCtas?.forEach((cta, i) => {
      cta.setAttribute('href', this.initialValuesOfExternalCtaHrefs[i])
      cta.setAttribute('aria-label', this.initialValuesOfExternalCtaAriaLabels[i])
    })
  }

  hide(el) {
    el.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE, CLASSES.visuallyHidden)
    el.setAttribute('aria-hidden', true)
  }

  show(el) {
    el.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    setTimeout(() => {
      this.quizItemGroups[this.quizItemGroupIndex].classList.remove(CLASSES.visuallyHidden)
    }, 20)
    el.setAttribute('aria-hidden', false)
  }

  setTabIndex(val) {
    return (el) => el.setAttribute('tabindex', val)
  }

  toggleSelected = (event) => {
    const quizItem = event.target.closest(SELECTORS.quizItem)
    if (quizItem.classList.contains(CLASSES.selected)) {
      this.handleDeselectClick(event)
    } else {
      this.handleSelectClick(event)
    }
  }

  setImageAriaHidden (hidden) {
    return (event) => {
      const quizItem = event.target.closest(SELECTORS.quizItem)
      const quizButtonImage = quizItem.querySelector(SELECTORS.quizItemImage)
      quizButtonImage.setAttribute('aria-hidden', hidden)
    }
  }

  handleSelectClick = (event) => {
    const quizItem = event.target.closest(SELECTORS.quizItem)
    this.quizItems.forEach(this.deselectItem)
    if (quizItem) {
      this.selectItem(quizItem)
    }
  }

  handleDeselectClick = (event) => {
    const quizItem = event.target.closest(SELECTORS.quizItem)
    if (quizItem) {
      this.deselectItem(quizItem)
    }
  }

  // Esc key handler for closing modals
  escKeyHandler = (event) => {
    if (event.key === 'Escape' && this.isModalOpen) {
      event.preventDefault()
      this.closeModal()
    }
  }

  handleTabInModal = (event) => {
    const { target: el, key, shiftKey } = event
    const modal = el.closest(SELECTORS.quizItemModal)
    const isInsideQuizModal = !!modal
    if (isInsideQuizModal && key === 'Tab' && this.isModalOpen) {
      const focusableElements = modal.querySelectorAll('a:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex^="-"])')
      const lastFocusableEl = focusableElements[focusableElements.length - 1]
      if (el === lastFocusableEl) {
        event.preventDefault()
        focusableElements[0].focus()
      } else if (shiftKey && el === focusableElements[0]) {
        event.preventDefault()
        lastFocusableEl.focus()
      }
    }
  }
}

export default {
  selector: '.js-feelings-monster-quiz',
  Source: FeelingsMonsterQuiz
}
